import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, CircularProgress, Alert,
} from '@mui/material';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import { useSubscriptions } from '../../hooks/useSubscriptions';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { SubscriptionPlanAdminDto, CreateSubscriptionPlanRequest, UpdateSubscriptionPlanRequest } from '../../types/institute';

const EMPTY_CREATE: CreateSubscriptionPlanRequest = {
  name: '', code: '', description: '',
  monthlyPrice: 0, annualPrice: 0, currency: 'INR',
  isActive: true, isPublic: true, isRecommended: false,
  displayOrder: 0, maxStudents: 500, maxEmployees: 100, maxBranches: 1,
  allowMonthly: true, allowAnnual: true,
};

function PlanFormDialog({
  open,
  initial,
  onClose,
  onSave,
  saving,
}: {
  open: boolean;
  initial: CreateSubscriptionPlanRequest | null;
  onClose: () => void;
  onSave: (data: CreateSubscriptionPlanRequest) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<CreateSubscriptionPlanRequest>(initial ?? EMPTY_CREATE);
  const isEdit = !!initial?.code && initial.code !== '';

  useEffect(() => {
    if (open) setForm(initial ?? EMPTY_CREATE);
  }, [open, initial]);

  const set = (field: keyof CreateSubscriptionPlanRequest, val: unknown) =>
    setForm((f) => ({ ...f, [field]: val }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{isEdit ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} size="small" fullWidth required />
          <TextField
            label="Code"
            value={form.code}
            onChange={(e) => set('code', e.target.value.toUpperCase())}
            size="small"
            fullWidth
            required
            disabled={isEdit}
            helperText={isEdit ? 'Code cannot be changed' : 'e.g. STARTER'}
          />
        </Box>
        <TextField label="Description" value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} size="small" fullWidth multiline rows={2} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            label="Allow Monthly"
            control={<Switch checked={form.allowMonthly} onChange={(e) => set('allowMonthly', e.target.checked)} size="small" />}
          />
          <FormControlLabel
            label="Allow Annual"
            control={<Switch checked={form.allowAnnual} onChange={(e) => set('allowAnnual', e.target.checked)} size="small" />}
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
          <TextField
            label="Monthly Price (₹)"
            type="number"
            value={form.monthlyPrice}
            onChange={(e) => set('monthlyPrice', parseFloat(e.target.value) || 0)}
            size="small"
            fullWidth
            disabled={!form.allowMonthly}
          />
          <TextField
            label="Annual Price (₹)"
            type="number"
            value={form.annualPrice}
            onChange={(e) => set('annualPrice', parseFloat(e.target.value) || 0)}
            size="small"
            fullWidth
            disabled={!form.allowAnnual}
          />
          <TextField label="Currency" value={form.currency} onChange={(e) => set('currency', e.target.value)} size="small" fullWidth />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
          <TextField
            label="Max Students"
            type="number"
            value={form.maxStudents}
            onChange={(e) => set('maxStudents', parseInt(e.target.value) || 0)}
            size="small"
            fullWidth
          />
          <TextField
            label="Max Employees"
            type="number"
            value={form.maxEmployees}
            onChange={(e) => set('maxEmployees', parseInt(e.target.value) || 0)}
            size="small"
            fullWidth
          />
          <TextField
            label="Max Branches"
            type="number"
            value={form.maxBranches}
            onChange={(e) => set('maxBranches', parseInt(e.target.value) || 0)}
            size="small"
            fullWidth
          />
        </Box>
        <TextField
          label="Display Order"
          type="number"
          value={form.displayOrder}
          onChange={(e) => set('displayOrder', parseInt(e.target.value) || 0)}
          size="small"
          sx={{ width: 160 }}
        />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            label="Active"
            control={<Switch checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} size="small" />}
          />
          <FormControlLabel
            label="Public"
            control={<Switch checked={form.isPublic} onChange={(e) => set('isPublic', e.target.checked)} size="small" />}
          />
          <FormControlLabel
            label="Recommended"
            control={<Switch checked={form.isRecommended} onChange={(e) => set('isRecommended', e.target.checked)} size="small" />}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button
          variant="contained"
          size="small"
          disabled={saving || !form.name || !form.code}
          onClick={() => onSave(form)}
          startIcon={saving ? <CircularProgress size={13} /> : undefined}
        >
          {isEdit ? 'Save Changes' : 'Create Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PlansPage() {
  const { usePlans, useCreatePlan, useUpdatePlan, useDeletePlan } = useSubscriptions();
  const { data: plans, isLoading, error } = usePlans();
  const createMutation = useCreatePlan();
  const updateMutation = useUpdatePlan();
  const deleteMutation = useDeletePlan();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlanAdminDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SubscriptionPlanAdminDto | null>(null);

  useEffect(() => {
    if (createMutation.isSuccess || updateMutation.isSuccess) {
      setDialogOpen(false);
      setEditingPlan(null);
    }
  }, [createMutation.isSuccess, updateMutation.isSuccess]);

  useEffect(() => {
    if (deleteMutation.isSuccess) setDeleteTarget(null);
  }, [deleteMutation.isSuccess]);

  const handleCreate = () => {
    createMutation.reset();
    updateMutation.reset();
    setEditingPlan(null);
    setDialogOpen(true);
  };
  const handleEdit = (plan: SubscriptionPlanAdminDto) => {
    createMutation.reset();
    updateMutation.reset();
    setEditingPlan(plan);
    setDialogOpen(true);
  };

  const handleSave = (data: CreateSubscriptionPlanRequest) => {
    if (editingPlan) {
      updateMutation.mutate({ planId: editingPlan.id, request: data as UpdateSubscriptionPlanRequest });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id);
  };

  if (error) return <Alert severity="error">Failed to load plans</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Subscription Plans</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Global plans shown to students who self-subscribe
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<Plus size={14} />} onClick={handleCreate}>
          New Plan
        </Button>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' } }}>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell align="right">Monthly (₹)</TableCell>
              <TableCell align="right">Annual (₹)</TableCell>
              <TableCell>Limits</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}><Box sx={{ height: 16, bgcolor: 'action.hover', borderRadius: 1 }} /></TableCell>
                    ))}
                  </TableRow>
                ))
              : plans?.map((plan) => (
                  <TableRow key={plan.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {plan.isRecommended && <Star size={12} color="#F59E0B" fill="#F59E0B" />}
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                          {plan.name}
                        </Typography>
                      </Box>
                      {plan.description && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                          {plan.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{plan.code}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        ₹{plan.monthlyPrice.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        ₹{plan.annualPrice.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                        {plan.maxStudents} students · {plan.maxEmployees} staff
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip
                          label={plan.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            height: 18, fontSize: '0.65rem',
                            bgcolor: plan.isActive ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                            color: plan.isActive ? '#10B981' : '#EF4444',
                          }}
                        />
                        {!plan.isPublic && (
                          <Chip label="Hidden" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(plan)}>
                            <Pencil size={13} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteTarget(plan)} sx={{ color: 'error.main' }}>
                            <Trash2 size={13} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Paper>

      <PlanFormDialog
        open={dialogOpen}
        initial={editingPlan
          ? { ...editingPlan, description: editingPlan.description ?? '' }
          : null}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        saving={createMutation.isPending || updateMutation.isPending}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Plan"
        message={`Remove "${deleteTarget?.name}"? If students are active on this plan it will be deactivated instead of deleted.`}
        confirmLabel="Delete"
        severity="danger"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
